/**
 * ThemeContext - Theme Management Provider
 *
 * Provides theme state and persistence with backend sync.
 * Supports 'dark', 'light', and 'system' (follows OS preference).
 */

import { createContext, useContext, useState, useEffect, useMemo, ReactNode, useCallback } from 'react';
import { auth } from '../lib/firebase/config';

type ThemePreference = 'dark' | 'light' | 'system';
type Theme = 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  preference: ThemePreference;
  setPreference: (pref: ThemePreference) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

function getSystemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(preference: ThemePreference): Theme {
  return preference === 'system' ? getSystemTheme() : preference;
}

export function ThemeProvider({ children, defaultTheme = 'dark' }: ThemeProviderProps) {
  const [preference, setPreferenceState] = useState<ThemePreference>('dark');
  const [theme, setTheme] = useState<Theme>(defaultTheme);

  // Load preference from backend on mount
  useEffect(() => {
    const loadPreference = async () => {
      const user = auth.currentUser;
      if (!user) {
        setTheme('dark');
        return;
      }

      try {
        const token = await user.getIdToken();
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user-settings/theme`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          const pref = data.themePreference || 'dark';
          setPreferenceState(pref);
          setTheme(resolveTheme(pref));
        }
      } catch (error) {
        console.error('Failed to load theme preference:', error);
      }
    };

    loadPreference();
  }, []);

  // Listen for system theme changes when preference is 'system'
  useEffect(() => {
    if (preference !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setTheme(getSystemTheme());

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [preference]);

  const setPreference = useCallback(async (pref: ThemePreference) => {
    setPreferenceState(pref);
    setTheme(resolveTheme(pref));

    const user = auth.currentUser;
    if (!user) return;

    try {
      const token = await user.getIdToken();
      await fetch(`${import.meta.env.VITE_API_URL}/api/user-settings/theme`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ themePreference: pref })
      });
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setPreference(newTheme);
  }, [theme, setPreference]);

  const value = useMemo(
    () => ({
      theme,
      preference,
      setPreference,
      toggleTheme,
    }),
    [theme, preference, setPreference, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
